/**
 * Campaign Management Panel — Admin Dashboard
 *
 * List view with status badges, market filters, pagination,
 * and "New Campaign" modal. Row click navigates to detail page.
 *
 * API Endpoints:
 * - GET  /api/v1/admin/campaigns     — List campaigns (paginated, filterable)
 * - POST /api/v1/admin/campaigns     — Create campaign
 * - GET  /api/v1/admin/markets       — Markets dropdown
 *
 * @story SCRUM-1936 — Campaign Admin Frontend
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/admin/Pagination';
import Badge from '../../components/common/Badge';
import apiRequest from '../../services/api';

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', margin: 0 },
  btn: {
    padding: '8px 16px', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontWeight: '500', fontSize: '14px',
  },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  filterBar: { display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' },
  select: {
    padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', backgroundColor: '#fff',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#6b7280' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6' },
  row: { cursor: 'pointer' },
  rowArchived: { opacity: 0.55, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '48px 0', color: '#6b7280' },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '520px', maxHeight: '90vh', overflowY: 'auto',
  },
  fieldGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '13px', color: '#374151' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
    minHeight: '80px', resize: 'vertical',
  },
  error: { color: '#dc2626', fontSize: '13px', marginTop: '8px' },
  modalActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' },
};

const STATUS_COLORS = {
  draft: 'gray',
  active: 'green',
  paused: 'yellow',
  completed: 'blue',
  archived: 'gray',
};

export default function CampaignManagement() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [marketFilter, setMarketFilter] = useState('');
  const [markets, setMarkets] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const handleNavigate = (_id, path) => {
    if (path) { navigate(path); }
  };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (statusFilter) { params.set('status', statusFilter); }
      if (marketFilter) { params.set('market_id', marketFilter); }

      const res = await apiRequest(`/api/v1/admin/campaigns?${params.toString()}`);
      if (res.success) {
        setCampaigns(res.data || []);
        setTotal(res.total || 0);
      }
    } catch {
      // Error handled by apiRequest
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, marketFilter]);

  const fetchMarkets = useCallback(async () => {
    try {
      const res = await apiRequest('/api/v1/admin/markets');
      if (res.success) {
        setMarkets(res.data || []);
      }
    } catch {
      // optional — markets are for filtering
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { fetchMarkets(); }, [fetchMarkets]);

  const formatDate = (iso) => {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="campaigns"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Campaigns</h1>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={() => setShowModal(true)}
          >
            + New Campaign
          </button>
        </div>

        <div style={styles.filterBar}>
          <select
            style={styles.select}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>

          <select
            style={styles.select}
            value={marketFilter}
            onChange={(e) => { setMarketFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Markets</option>
            {markets.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading campaigns...</div>
        ) : campaigns.length === 0 ? (
          <div style={styles.empty}>
            No campaigns yet. Create your first campaign to get started.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Market(s)</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Channels</th>
                  <th style={styles.th}>Date Range</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    style={c.status === 'archived' ? styles.rowArchived : styles.row}
                    onClick={() => navigate(`/admin/marketing/campaigns/${c.id}`)}
                  >
                    <td style={styles.td}>{c.name}</td>
                    <td style={styles.td}>
                      {(c.markets || []).map((m) => m.market_name).join(', ') || '—'}
                    </td>
                    <td style={styles.td}>
                      <Badge color={STATUS_COLORS[c.status] || 'gray'}>
                        {c.status}
                      </Badge>
                    </td>
                    <td style={styles.td}>{c.channel_count || 0}</td>
                    <td style={styles.td}>
                      {formatDate(c.starts_at)} — {formatDate(c.ends_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      {showModal && (
        <CreateCampaignModal
          markets={markets}
          onClose={() => setShowModal(false)}
          onCreated={(campaign) => {
            setShowModal(false);
            navigate(`/admin/marketing/campaigns/${campaign.id}`);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function CreateCampaignModal({ markets, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    market_ids: [],
    starts_at: '',
    ends_at: '',
    hubspot_campaign_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        market_ids: form.market_ids.length > 0 ? form.market_ids : undefined,
        starts_at: form.starts_at || undefined,
        ends_at: form.ends_at || undefined,
        hubspot_campaign_id: form.hubspot_campaign_id.trim() || undefined,
      };
      const res = await apiRequest('/api/v1/admin/campaigns', {
        method: 'POST',
        body,
      });
      if (res.success) {
        onCreated(res.data);
      } else {
        setError(res.error || 'Failed to create campaign');
      }
    } catch (err) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const toggleMarket = (marketId) => {
    setForm((prev) => ({
      ...prev,
      market_ids: prev.market_ids.includes(marketId)
        ? prev.market_ids.filter((id) => id !== marketId)
        : [...prev.market_ids, marketId],
    }));
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>New Campaign</h2>
        <form onSubmit={handleSubmit}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Name *</label>
            <input
              style={styles.input}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Summer 2026 Colorado Launch"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              style={styles.textarea}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Campaign goals and notes..."
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Markets</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {markets.map((m) => (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.market_ids.includes(m.id)}
                    onChange={() => toggleMarket(m.id)}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...styles.fieldGroup, flex: 1 }}>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                style={styles.input}
                value={form.starts_at}
                onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
              />
            </div>
            <div style={{ ...styles.fieldGroup, flex: 1 }}>
              <label style={styles.label}>End Date</label>
              <input
                type="date"
                style={styles.input}
                value={form.ends_at}
                onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>HubSpot Campaign ID</label>
            <input
              style={styles.input}
              value={form.hubspot_campaign_id}
              onChange={(e) => setForm((f) => ({ ...f, hubspot_campaign_id: e.target.value }))}
              placeholder="Optional"
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.modalActions}>
            <button
              type="button"
              style={{ ...styles.btn, backgroundColor: '#f3f4f6', color: '#374151' }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ ...styles.btn, ...styles.btnPrimary }}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
